import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Printer,
  ShieldCheck,
  FileDiff,
  CheckCircle2,
  AlertTriangle,
  Gauge,
  ListChecks,
  Layers,
} from "lucide-react";

const heading = { fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" };
const mono = { fontFamily: "'JetBrains Mono', Menlo, Consolas, monospace" };

const SEV = {
  CRITICAL: "sev-critical text-red-400 border-red-400/40 bg-red-400/10",
  HIGH: "sev-high text-orange-400 border-orange-400/40 bg-orange-400/10",
  MEDIUM: "sev-medium text-amber-400 border-amber-400/40 bg-amber-400/10",
  LOW: "sev-low text-teal-300 border-teal-400/40 bg-teal-400/10",
};

const findings = [
  {
    id: "NDX-001",
    severity: "CRITICAL",
    title: "Sensitive configuration is insufficiently isolated",
    area: "Configuration / Security",
    files: ["backend/config.py", "backend/services/ai_client.py", ".env.example"],
    description:
      "Configuration values and service initialization are too closely coupled with application logic. In a production system this can make secrets, environment-specific behavior and deployment configuration harder to control safely.",
    impact: [
      "accidental exposure of sensitive configuration",
      "inconsistent behavior between environments",
      "difficult secret rotation",
      "higher deployment risk",
    ],
    recommendation:
      "Move environment access into a dedicated validated configuration layer. Application services should receive validated configuration rather than reading environment state throughout the codebase.",
    priority: "Immediate",
  },
  {
    id: "NDX-002",
    severity: "HIGH",
    title: "API and business logic responsibilities are mixed",
    area: "Architecture",
    files: ["backend/server.py", "backend/services/project_service.py"],
    description:
      "Some request-handling paths contain application rules that should live inside dedicated service modules.",
    impact: ["larger route handlers", "difficult testing", "duplicated logic", "increased regression risk"],
    recommendation:
      "Move domain behavior into service-level functions and keep HTTP route handlers focused on validation, orchestration and response formatting.",
    priority: "High",
  },
  {
    id: "NDX-003",
    severity: "HIGH",
    title: "Critical workflows lack automated tests",
    area: "Verification",
    files: ["tests/", "backend/services/"],
    description:
      "Core workflows have insufficient automated coverage, making regressions more likely when AI-assisted modifications are made.",
    impact: [
      "silent regressions",
      "slower debugging",
      "reduced confidence when refactoring",
      "increasing cost as the project grows",
    ],
    recommendation: "Add focused tests around the highest-value workflows before expanding the feature surface.",
    priority: "High",
  },
  {
    id: "NDX-004",
    severity: "HIGH",
    title: "Dependency versions are insufficiently constrained",
    area: "Dependencies",
    files: ["requirements.txt", "package.json"],
    description:
      "Some dependencies allow a broader update range than desirable for a rapidly evolving production application.",
    impact: ["unexpected dependency changes", "build instability", "difficult reproduction of production issues"],
    recommendation:
      "Review direct dependencies, constrain production-critical versions and introduce a controlled upgrade process.",
    priority: "High",
  },
  {
    id: "NDX-005",
    severity: "MEDIUM",
    title: "Error handling is inconsistent across service boundaries",
    area: "Reliability",
    files: [],
    description: "Different components handle failures using inconsistent patterns.",
    impact: [],
    recommendation: "Introduce a consistent error boundary and structured application errors.",
    priority: "Medium",
  },
];

const phases = [
  { name: "PHASE 1 — Stabilize", items: ["Configuration isolation", "Secret/environment validation", "Dependency stabilization"] },
  { name: "PHASE 2 — Protect", items: ["Critical workflow tests", "Consistent error handling", "Verification commands"] },
  { name: "PHASE 3 — Improve Architecture", items: ["Extract service logic from API handlers", "Reduce duplication", "Clarify module responsibilities"] },
  { name: "PHASE 4 — Continue Product Development", items: ["Only after the high-priority risks have been addressed."] },
];

const verification = [
  { name: "Python compilation", status: "PASS REQUIRED" },
  { name: "Automated tests", status: "PASS REQUIRED" },
  { name: "Lint / static analysis", status: "PASS REQUIRED" },
  { name: "Configuration validation", status: "PASS REQUIRED" },
  { name: "Critical workflow regression tests", status: "PASS REQUIRED" },
  { name: "Manual smoke test", status: "RECOMMENDED" },
];

const printCss = `
.print-only { display: none; }
@media print {
  @page { size: A4; margin: 14mm; }
  html, body { background: #ffffff !important; }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  .sample-report { background: #ffffff !important; }
  .sample-report * {
    background: transparent !important;
    color: #0f172a !important;
    box-shadow: none !important;
    text-shadow: none !important;
    border-color: #cbd5e1 !important;
  }
  .sample-report .avoid-break { break-inside: avoid; page-break-inside: avoid; }
  .sample-report .page-break { break-before: page; page-break-before: always; }
  .sample-report .sev-critical { color: #b91c1c !important; border-color: #b91c1c !important; }
  .sample-report .sev-high { color: #c2410c !important; border-color: #c2410c !important; }
  .sample-report .sev-medium { color: #a16207 !important; border-color: #a16207 !important; }
  .sample-report .sev-low { color: #0f766e !important; border-color: #0f766e !important; }
  .sample-report .print-accent { color: #0f766e !important; }
  .sample-report .print-muted { color: #64748b !important; }
  .sample-report .diff-del { color: #b91c1c !important; }
  .sample-report .diff-add { color: #047857 !important; }
  .print-footer {
    display: block !important;
    position: fixed; bottom: -8mm; left: 0; right: 0;
    text-align: center; font-size: 9px; color: #64748b !important;
  }
}
`;

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-50 tracking-tight" style={heading}>
    {children}
  </h2>
);

const Card = ({ children, className = "", ...props }) => (
  <div className={`rounded-2xl border border-slate-800 bg-slate-900/40 avoid-break ${className}`} {...props}>
    {children}
  </div>
);

const SevBadge = ({ sev }) => (
  <span className={`inline-flex px-2.5 py-1 rounded-full border text-[11px] font-bold tracking-wider ${SEV[sev]}`}>{sev}</span>
);

const MetricCard = ({ label, value, tone = "text-slate-50" }) => (
  <Card className="p-5">
    <p className="text-xs text-slate-400 print-muted uppercase tracking-wider font-semibold">{label}</p>
    <p className={`mt-2 text-lg font-bold ${tone}`} style={heading}>
      {value}
    </p>
  </Card>
);

export default function SampleReport() {
  return (
    <div className="sample-report min-h-screen bg-slate-950 text-slate-100 antialiased" data-testid="sample-report-page">
      <style>{printCss}</style>

      {/* Screen header (hidden in print) */}
      <header className="no-print sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/developer-audit" className="flex items-center gap-3" data-testid="report-logo-link">
            <img src="/nephrix-logo.png" alt="Nephrix" className="h-9 w-auto" />
            <span className="text-slate-100 font-semibold tracking-tight hidden sm:flex items-baseline gap-2" style={heading}>
              Nephrix <span className="text-teal-300 text-xs font-bold tracking-widest uppercase">Developer</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/developer-audit" className="hidden sm:flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="report-back-link">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm transition-colors"
              data-testid="download-report-btn"
            >
              <Printer className="w-4 h-4" /> Download Sample Report
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16 space-y-14">
        {/* ===== Report identity ===== */}
        <section className="avoid-break">
          <p className="text-xs font-bold tracking-[0.25em] text-teal-300 print-accent uppercase">Nephrix Developer</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold text-slate-50 tracking-tight" style={heading} data-testid="report-title">
            Developer Audit Report
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-400/30 text-teal-300 print-accent text-xs font-bold tracking-widest uppercase" data-testid="sample-badge">
              Sample Report
            </span>
            <span className="inline-flex px-4 py-1.5 rounded-full bg-slate-800/70 border border-slate-700 text-slate-300 text-xs font-semibold tracking-wider uppercase" data-testid="fictional-badge">
              Fictional project — for demonstration purposes
            </span>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Project" value="Atlas AI" />
            <MetricCard label="Audit type" value="Deep Audit" />
            <MetricCard label="Audit date" value="01 June 2026 (sample)" />
            <MetricCard label="Report version" value="1.0 Sample" />
          </div>
        </section>

        {/* ===== Executive dashboard ===== */}
        <section className="avoid-break">
          <SectionTitle>Executive dashboard</SectionTitle>
          <p className="mt-2 text-xs text-slate-400 print-muted">All scores and findings below are fictional sample data.</p>
          <div className="mt-6 grid md:grid-cols-[1.2fr,2fr] gap-4">
            <Card className="p-7 flex flex-col justify-center items-center text-center">
              <Gauge className="w-6 h-6 text-teal-300 print-accent" />
              <p className="mt-3 text-5xl font-extrabold text-slate-50" style={heading} data-testid="health-score">
                68<span className="text-2xl text-slate-400 print-muted"> / 100</span>
              </p>
              <p className="mt-2 text-xs text-slate-400 print-muted uppercase tracking-wider font-semibold">Overall Project Health</p>
              <span className={`mt-4 inline-flex px-3 py-1 rounded-full border text-xs font-bold tracking-wider ${SEV.HIGH}`}>
                RISK: MODERATE–HIGH
              </span>
            </Card>
            <div className="grid grid-cols-2 gap-4">
              <MetricCard label="Critical issues" value="1" tone="text-red-400 sev-critical" />
              <MetricCard label="High priority" value="3" tone="text-orange-400 sev-high" />
              <MetricCard label="Medium priority" value="5" tone="text-amber-400 sev-medium" />
              <MetricCard label="Low priority" value="2" tone="text-teal-300 sev-low" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Automated verification coverage" value="LIMITED" tone="text-amber-400 sev-medium" />
            <MetricCard label="Architecture stability" value="NEEDS REVIEW" tone="text-orange-400 sev-high" />
            <MetricCard label="Configuration health" value="AT RISK" tone="text-red-400 sev-critical" />
            <MetricCard label="Test coverage" value="INSUFFICIENT" tone="text-orange-400 sev-high" />
          </div>
        </section>

        {/* ===== Executive summary ===== */}
        <section className="avoid-break">
          <SectionTitle>Executive summary</SectionTitle>
          <Card className="mt-6 p-7 space-y-4 text-[15px] text-slate-300 leading-relaxed">
            <p>
              Atlas AI is functional and suitable for continued development, but the sample audit identifies several
              structural, configuration and verification risks that could significantly increase maintenance cost as
              the application grows.
            </p>
            <p>
              The most important concerns are concentrated around configuration management, separation of
              responsibilities and insufficient automated verification of critical application paths.
            </p>
            <p className="text-slate-100 font-semibold">
              The project does not require a full rewrite. The recommended strategy is targeted remediation: stabilize
              the highest-risk areas first, introduce verification around critical behavior and only then continue
              significant feature expansion.
            </p>
          </Card>
        </section>

        {/* ===== Top findings ===== */}
        <section className="page-break">
          <SectionTitle>Top findings</SectionTitle>
          <div className="mt-6 space-y-6">
            {findings.map((f) => (
              <Card key={f.id} className="p-7" data-testid={`finding-${f.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs font-bold text-slate-400 print-muted tracking-widest" style={mono}>
                    {f.id}
                  </span>
                  <SevBadge sev={f.severity} />
                  <span className="text-xs text-slate-400 print-muted">Area: <span className="text-slate-200 font-semibold">{f.area}</span></span>
                  <span className="ml-auto text-xs text-slate-400 print-muted">Priority: <span className="text-slate-200 font-semibold">{f.priority}</span></span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-50" style={heading}>
                  {f.title}
                </h3>
                {f.files.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {f.files.map((file) => (
                      <span key={file} className="px-2.5 py-1 rounded-md bg-slate-950/60 border border-slate-800 text-slate-300 text-xs" style={mono}>
                        {file}
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">{f.description}</p>
                {f.impact.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-bold text-slate-400 print-muted uppercase tracking-wider">Potential impact</p>
                    <ul className="mt-2 space-y-1.5">
                      {f.impact.map((i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 sev-medium shrink-0 mt-0.5" />
                          {i}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-4 rounded-xl border border-teal-400/20 bg-teal-500/[0.06] p-4">
                  <p className="text-xs font-bold text-teal-300 print-accent uppercase tracking-wider">Recommendation</p>
                  <p className="mt-1.5 text-sm text-slate-200 leading-relaxed">{f.recommendation}</p>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* ===== Remediation priority ===== */}
        <section className="page-break">
          <SectionTitle>Remediation priority</SectionTitle>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {phases.map((ph, pi) => (
              <Card key={ph.name} className="p-6">
                <p className="text-sm font-bold text-teal-300 print-accent tracking-wider" style={heading}>
                  {ph.name}
                </p>
                <ul className="mt-3 space-y-2">
                  {ph.items.map((item, ii) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                      {pi < 3 ? (
                        <span className="shrink-0 w-5 h-5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-bold flex items-center justify-center">
                          {pi * 3 + ii + 1}
                        </span>
                      ) : (
                        <ListChecks className="w-4 h-4 text-teal-400 print-accent shrink-0 mt-0.5" />
                      )}
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <Card className="mt-4 p-6">
            <p className="text-xs font-bold text-slate-400 print-muted uppercase tracking-wider">Illustrative effort bands (sample estimates)</p>
            <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-center">
                <p className="font-bold text-slate-100">Small</p>
                <p className="text-slate-400 print-muted text-xs mt-1">&lt; 2 hours</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-center">
                <p className="font-bold text-slate-100">Medium</p>
                <p className="text-slate-400 print-muted text-xs mt-1">2–6 hours</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-center">
                <p className="font-bold text-slate-100">Large</p>
                <p className="text-slate-400 print-muted text-xs mt-1">1+ development day</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400 print-muted">Real estimates depend on the actual codebase.</p>
          </Card>
        </section>

        {/* ===== Patch proposal ===== */}
        <section className="avoid-break">
          <div className="flex flex-wrap items-center gap-3">
            <SectionTitle>Nephrix Patch Proposal</SectionTitle>
            <span className="inline-flex px-3 py-1 rounded-full bg-teal-500/10 border border-teal-400/30 text-teal-300 print-accent text-[11px] font-bold tracking-widest uppercase">
              Read-only preview
            </span>
          </div>
          <Card className="mt-6 overflow-hidden" data-testid="patch-proposal">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-4 border-b border-slate-800 bg-slate-950/50 text-xs">
              <span className="text-slate-400 print-muted">
                Target: <span className="text-slate-100 font-semibold" style={mono}>backend/config.py</span>
              </span>
              <span className="text-slate-400 print-muted">
                Risk: <span className="font-bold text-amber-400 sev-medium">MEDIUM</span>
              </span>
              <span className="text-slate-400 print-muted">
                Confirmation: <span className="font-bold text-slate-100">REQUIRED</span>
              </span>
              <span className="ml-auto flex items-center gap-1.5 text-teal-300 print-accent font-semibold">
                <FileDiff className="w-3.5 h-3.5" /> diff preview
              </span>
            </div>
            <div className="p-5 text-[13px] leading-relaxed" style={mono}>
              <p className="diff-del text-red-400">- OPENAI_API_KEY = os.getenv("OPENAI_API_KEY")</p>
              <p className="diff-del text-red-400">- DATABASE_URL = os.getenv("DATABASE_URL")</p>
              <p className="diff-add text-emerald-400">+ class Settings:</p>
              <p className="diff-add text-emerald-400">+     def __init__(self):</p>
              <p className="diff-add text-emerald-400">+         self.openai_api_key = require_env("OPENAI_API_KEY")</p>
              <p className="diff-add text-emerald-400">+         self.database_url = require_env("DATABASE_URL")</p>
              <p className="diff-add text-emerald-400">+</p>
              <p className="diff-add text-emerald-400">+ settings = Settings()</p>
            </div>
            <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/50 space-y-2">
              <p className="text-xs text-slate-400 print-muted leading-relaxed">
                This sample illustrates the Patch Proposal format. A real Nephrix audit only proposes changes supported
                by the inspected project context.
              </p>
              <p className="flex items-center gap-2 text-xs font-semibold text-teal-300 print-accent">
                <ShieldCheck className="w-4 h-4" /> No code is modified by generating a Patch Proposal.
              </p>
            </div>
          </Card>
        </section>

        {/* ===== Verification plan ===== */}
        <section className="avoid-break">
          <SectionTitle>Verification plan</SectionTitle>
          <Card className="mt-6 divide-y divide-slate-800">
            {verification.map((v) => (
              <div key={v.name} className="flex items-center justify-between px-6 py-3.5">
                <span className="flex items-center gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-teal-400 print-accent" />
                  {v.name}
                </span>
                <span
                  className={`text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full border ${
                    v.status === "RECOMMENDED" ? SEV.LOW : SEV.HIGH
                  }`}
                >
                  {v.status}
                </span>
              </div>
            ))}
          </Card>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            A remediation is not considered complete simply because code changed. Verification determines whether the
            change safely improved the project.
          </p>
        </section>

        {/* ===== Architecture observations ===== */}
        <section className="avoid-break">
          <SectionTitle>Architecture observations</SectionTitle>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <Card className="p-6">
              <p className="text-sm font-bold text-teal-300 print-accent uppercase tracking-wider flex items-center gap-2">
                <Layers className="w-4 h-4" /> Strengths
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>• recognizable modular structure</li>
                <li>• existing service boundaries in parts of the project</li>
                <li>• suitable foundation for incremental improvement</li>
              </ul>
            </Card>
            <Card className="p-6">
              <p className="text-sm font-bold text-orange-400 sev-high uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Weaknesses
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>• responsibilities are not consistently separated</li>
                <li>• verification maturity is behind feature development</li>
                <li>• configuration requires stronger boundaries</li>
              </ul>
            </Card>
          </div>
          <Card className="mt-4 p-6">
            <p className="text-sm text-slate-200">
              <span className="font-bold text-slate-50">Conclusion:</span> Incremental remediation is recommended. A
              full rewrite is not justified by the sample findings.
            </p>
          </Card>
        </section>

        {/* ===== What we would fix first ===== */}
        <section className="avoid-break">
          <Card className="p-7 border-teal-400/25 bg-teal-500/[0.06]" data-testid="fix-first-box">
            <p className="text-sm font-bold text-teal-300 print-accent uppercase tracking-wider">
              If this were a real project, Nephrix would recommend:
            </p>
            <div className="mt-4 space-y-3 text-sm">
              {[
                ["FIRST", "Protect secrets and configuration."],
                ["SECOND", "Protect critical workflows with tests."],
                ["THIRD", "Reduce coupling between API and business logic."],
                ["THEN", "Continue new feature development."],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start gap-4">
                  <span className="shrink-0 w-20 text-xs font-bold text-slate-400 print-muted tracking-widest pt-0.5">{k}</span>
                  <span className="text-slate-100 font-semibold">{v}</span>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* ===== Final assessment ===== */}
        <section className="avoid-break">
          <SectionTitle>Final assessment</SectionTitle>
          <Card className="mt-6 p-7">
            <p className="text-xl font-bold text-slate-50 leading-snug" style={heading}>
              The project is recoverable. <span className="text-teal-300 print-accent">The priority is stabilization, not rebuilding.</span>
            </p>
            <p className="mt-4 text-[15px] text-slate-300 leading-relaxed">
              Atlas AI demonstrates the type of project where targeted technical remediation can produce significantly
              more value than adding another layer of features. The immediate objective should be to reduce uncertainty
              around configuration, verification and architectural boundaries.
            </p>
          </Card>
        </section>

        {/* ===== Upsell CTA (screen only) ===== */}
        <section className="no-print">
          <div className="relative overflow-hidden rounded-3xl border border-teal-400/25 bg-slate-900/60 p-10 md:p-12 text-center">
            <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[420px] h-[220px] rounded-full bg-teal-500/15 blur-[100px]" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight" style={heading}>
                Know what is wrong. <span className="text-teal-300">Now decide what to fix.</span>
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-xl mx-auto">
                A Nephrix Deep Audit gives you the diagnosis and remediation order. If you want assistance implementing
                the highest-priority changes, request a Nephrix Fix Sprint.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/developer-audit#pricing"
                  className="group inline-flex items-center gap-2 px-7 py-4 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-full transition-all shadow-[0_10px_40px_-10px_rgba(20,184,166,0.6)]"
                  data-testid="report-deep-audit-cta"
                >
                  Get My Deep Audit — €249
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/developer-audit/intake?package=fix-sprint"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-100 font-semibold rounded-full transition-colors"
                  data-testid="report-fix-sprint-cta"
                >
                  Request Fix Sprint
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Disclaimer ===== */}
        <section className="avoid-break pb-8">
          <p className="text-xs text-slate-500 print-muted leading-relaxed border-t border-slate-800 pt-6" data-testid="report-disclaimer">
            This is a fictional sample report created solely to demonstrate the format and level of detail of a Nephrix
            Developer Audit. Atlas AI is not presented as a real customer or audited company. Actual findings, scores,
            remediation recommendations and turnaround depend on the submitted project.
          </p>
          <p className="print-only mt-2 text-xs">Contact: beta@nephrixapp.com · nephrixapp.com/developer-audit</p>
        </section>
      </main>

      <div className="print-footer">nephrixapp.com · Nephrix Developer Audit — Sample Report (fictional demonstration)</div>
    </div>
  );
}
